import {
  BulkBgUpdateDialogState,
  ConfiguredPaymentMethod,
  Gateway,
  PaymentGatewayPriority,
  SelectionArgs
} from 'src/lib/types/paymentSettings'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useUser } from 'src/lib/contexts/UserContext'
import {
  getNormalizedPaymentMethods,
  getNormalizedAllowedGateways,
  INITIAL_BULK_PG_UPDATE_STATE
} from 'src/lib/utils/paymentSettings'
import { IPaymentGateways } from 'src/lib/types/settings'
import { PAYMENT_GATEWAY, PAYMENT_METHOD } from 'src/assets/SettingsConfig'
import PaymentGatewaySelectors from './PaymentGatewaySelectors'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import BulkPGUpdateDialog from './BulkPGUpdateDialog'
import { permissionType } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'

const normalizedPaymentMethods: Record<string, IPaymentGateways> = getNormalizedPaymentMethods()
const normalizedAllowedGateways: Record<string, IPaymentGateways> = getNormalizedAllowedGateways()

const PaymentMethodRow: React.FC<ConfiguredPaymentMethod> = memo(
  ({
    availableGateways,
    primaryGateway,
    secondaryGateway,
    method,
    shouldOnlyShowText,
    isComingSoon,
    isDisabled,
    onGatewaySelect
  }) => {
    const { t } = useLocale()

    const methodName = normalizedPaymentMethods[method].label
    const methodImage = normalizedPaymentMethods[method].imgPath

    const { canAccess } = useUser()
    const hasAutoPGAccess = canAccess(permissionType.AUTO_PG_ROUTING, 0)
    const hasMultiPGAccess = canAccess(permissionType.MULTI_PG_SUPPORT, 0)
    const primaryAllowedGateways = availableGateways?.filter(gateway => gateway !== secondaryGateway?.pg_name)
    const secondaryAllowedGateways = availableGateways?.filter(gateway => gateway !== primaryGateway?.pg_name)

    const [bulkUpdateDialogState, setBulkUpdateDialogState] =
      useState<BulkBgUpdateDialogState>(INITIAL_BULK_PG_UPDATE_STATE)
    const [isRemovalConfirmOpen, setIsRemovalConfirmOpen] = useState<boolean>(false)

    const handleGateWaySelect = useCallback(
      async (args: SelectionArgs) => {
        if (!hasMultiPGAccess && !args.isRemoval && args.gateway) {
          setBulkUpdateDialogState({
            isOpen: true,
            gateway: args.gateway,
            priority: args.priority
          })
          return
        }
        return onGatewaySelect(args)
      },
      [hasMultiPGAccess, onGatewaySelect]
    )

    return (
      <>
        <li className='grid grid-cols-3 divide-x-1 text-sm font-medium text-coal-dark'>
          <span className='my-auto p-3 flex items-center space-x-1'>
            {methodImage && <img width={36} height={36} alt={methodName} src={methodImage} className='rounded-md' />}
            <span>{methodName}</span>
          </span>
          <PaymentGatewaySelectors
            method={method}
            availableGateways={availableGateways || []}
            primaryGateway={primaryGateway}
            secondaryGateway={secondaryGateway}
            isDisabled={!!isDisabled}
            hasAutoPGAccess={hasAutoPGAccess}
            shouldOnlyShowText={!!shouldOnlyShowText}
            isComingSoon={!!isComingSoon}
            normalizedAllowedGateways={normalizedAllowedGateways}
            handleGateWaySelect={handleGateWaySelect}
            onGatewaySelect={onGatewaySelect}
            setIsRemovalConfirmOpen={setIsRemovalConfirmOpen}
          />
        </li>

        <FloGenericDialog
          width={510}
          isOpen={bulkUpdateDialogState.isOpen}
          handleClose={() => {
            setBulkUpdateDialogState(INITIAL_BULK_PG_UPDATE_STATE)
          }}
          handleJob={async () => {
            if (bulkUpdateDialogState.isOpen) {
              onGatewaySelect({
                method,
                priority: bulkUpdateDialogState.priority,
                isRemoval: false,
                gateway: bulkUpdateDialogState.gateway,
                swapGateways: false,
                bulkUpdate: true
              })
              setBulkUpdateDialogState(INITIAL_BULK_PG_UPDATE_STATE)
            }
          }}
          jobButtonText={
            bulkUpdateDialogState.isOpen
              ? `Change all to ${normalizedAllowedGateways[bulkUpdateDialogState.gateway].label}`
              : ''
          }
          closeButtonText='Cancel'
          content={<BulkPGUpdateDialog />}
          title={
            <>
              <img src={'/images/badges/Info.svg'} alt='info' />
            </>
          }
        />
        {isRemovalConfirmOpen && (
          <ConfirmationDialog
            isOpen={isRemovalConfirmOpen}
            setIsConfirmationOpen={setIsRemovalConfirmOpen}
            title='Remove alternate payment gateway'
            confirmationText={`Are you sure you want to remove the alternate payment gateway for ${normalizedPaymentMethods[method].label}?`}
            jobButton='Yes, remove'
            cancelButton='No'
            jobButtonColor='error'
            cancelButtonColor='secondary'
            handleJob={() => {
              handleGateWaySelect({
                method,
                priority: PaymentGatewayPriority.SECONDARY,
                isRemoval: true
              })
            }}
          />
        )}
      </>
    )
  }
)

export default PaymentMethodRow
